/**
 * FocusWidget — Shows the user's single highest-priority task with adaptive coaching
 * Designed to sit on the main dashboard, keeping users focused on ONE thing.
 */
import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { API_URL } from '@/config/apiConfig';
import {
  Target, Brain, Clock, AlertTriangle, TrendingUp, CheckCircle2,
  ChevronDown, ChevronUp, Loader2, Zap
} from 'lucide-react';

export default function FocusWidget() {
  const [data, setData] = useState(null);
  const [showCoaching, setShowCoaching] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/coaching/adaptive/my-focus`, { credentials: 'include', 
          headers: {}
        });
        if (res.ok) setData(await res.json());
      } catch (_) {}
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return null;
  if (!data?.has_focus) return null;

  const f = data.focus;
  const c = data.coaching;
  const w = data.workload;

  const toneColors = {
    encouraging: 'border-blue-500/20 bg-blue-500/5',
    motivating: 'border-emerald-500/20 bg-emerald-500/5',
    supportive: 'border-amber-500/20 bg-amber-500/5'
  };

  const toneIcons = {
    encouraging: Target,
    motivating: TrendingUp,
    supportive: AlertTriangle
  };

  const ToneIcon = toneIcons[c?.tone] || Target;

  return (
    <div className={`rounded-xl border p-4 ${toneColors[c?.tone] || toneColors.encouraging}`} data-testid="focus-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ToneIcon className="w-4 h-4 text-primary" />
          <span className="text-xs font-bold uppercase tracking-wider text-primary">Your Focus</span>
        </div>
        {w?.overloaded && (
          <Badge variant="destructive" className="text-[10px]">{w.active_tasks} active tasks</Badge>
        )}
      </div>

      {/* Focus Task */}
      <div className="mb-3">
        <p className="text-sm font-semibold">{f.title}</p>
        <p className="text-xs text-muted-foreground mt-1">{f.detail}</p>
        <div className="flex items-center gap-2 mt-2">
          <Badge variant="outline" className="text-[10px]">{f.plan_title}</Badge>
          <Badge variant="secondary" className="text-[10px]">{f.expected_impact}</Badge>
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${f.effort === 'Low' ? 'bg-emerald-500/10 text-emerald-600' : f.effort === 'Medium' ? 'bg-amber-500/10 text-amber-600' : 'bg-red-500/10 text-red-600'}`}>{f.effort}</span>
          {f.overdue && <Badge variant="destructive" className="text-[10px]">{f.days_overdue}d overdue</Badge>}
        </div>
      </div>

      {/* Priority Message */}
      {c?.priority_message && (
        <p className="text-xs text-muted-foreground italic mb-3">{c.priority_message}</p>
      )}

      {/* Coaching Toggle */}
      <button
        onClick={() => setShowCoaching(!showCoaching)}
        className="w-full flex items-center justify-between text-xs text-primary hover:underline"
      >
        <span className="flex items-center gap-1"><Brain className="w-3 h-3" /> Coaching Guide</span>
        {showCoaching ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {showCoaching && c && (
        <div className="mt-3 space-y-2">
          {c.personalised_tips?.length > 0 && (
            <div className="space-y-1">
              {c.personalised_tips.map((tip, i) => (
                <div key={`${tip?.id ?? tip?.name ?? tip?.title ?? 'tip'}-${i}`} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Zap className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          )}
          {c.next_milestone && (
            <div className="p-2 rounded-lg bg-background border text-xs">
              <span className="font-semibold text-primary">{c.next_milestone.week}:</span> <span className="text-muted-foreground">{c.next_milestone.expect}</span>
            </div>
          )}
          {c.base_coaching?.cory_prompt && (
            <a href={`/app?cory=${encodeURIComponent(c.base_coaching.cory_prompt)}`} className="flex items-center gap-1 text-xs text-purple-500 hover:underline">
              <Brain className="w-3 h-3" /> Ask Cory about this
            </a>
          )}
          {c.workload_note && (
            <p className="text-[10px] text-muted-foreground">{c.workload_note}</p>
          )}
        </div>
      )}
    </div>
  );
}
